// crashLog.ts — Global error capture for diagnosing 'black screen' crashes.
//
// Persists the most recent uncaught errors and unhandled promise
// rejections to localStorage so a user who just experienced a crash
// can pull them out and report them. Surfaced in the Settings page
// (and in any error-boundary fallback that wants to expose them).
//
// Boss report 2026-05-18 21:56 PT: clicking a saved track log blanks
// the app to black with no visible error. The TrackDetailErrorBoundary
// only catches synchronous render exceptions; iOS Safari swallows
// async/WebGL errors silently. This logger captures both.

interface CrashEntry {
  at: number;
  kind: 'error' | 'rejection';
  message: string;
  stack?: string;
  filename?: string;
  lineno?: number;
  colno?: number;
  url?: string;
}

const KEY = 'fyrefly:crashLog:v1';
const MAX_ENTRIES = 20;

function read(): CrashEntry[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const v = JSON.parse(raw);
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

function write(entries: CrashEntry[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(entries.slice(-MAX_ENTRIES)));
  } catch {
    // quota / private mode — best effort
  }
}

function push(entry: CrashEntry): void {
  const cur = read();
  cur.push(entry);
  write(cur);
  // Also log to console so devtools / iOS Web Inspector users see it
  // immediately if they happen to be watching.
  // eslint-disable-next-line no-console
  console.error('[fyrefly:crashLog]', entry);
}

let installed = false;

export function installCrashLog(): void {
  if (installed) return;
  if (typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (e) => {
    push({
      at: Date.now(),
      kind: 'error',
      message: e.message ?? String(e.error ?? 'unknown error'),
      stack: e.error?.stack,
      filename: e.filename,
      lineno: e.lineno,
      colno: e.colno,
      url: typeof location !== 'undefined' ? location.href : undefined,
    });
  });

  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason;
    const message =
      reason instanceof Error
        ? reason.message
        : typeof reason === 'string'
          ? reason
          : (() => { try { return JSON.stringify(reason); } catch { return String(reason); } })();
    push({
      at: Date.now(),
      kind: 'rejection',
      message,
      stack: reason instanceof Error ? reason.stack : undefined,
      url: typeof location !== 'undefined' ? location.href : undefined,
    });
  });
}

export function getCrashLog(): CrashEntry[] {
  return read();
}

export function clearCrashLog(): void {
  try { localStorage.removeItem(KEY); } catch { /* noop */ }
}

/** Formatted multiline string suitable for copy-paste to chat. */
export function formatCrashLog(): string {
  const entries = read();
  if (entries.length === 0) return '(no crashes logged)';
  return entries
    .map((e, i) => {
      const ts = new Date(e.at).toISOString();
      const loc = e.filename ? ` @${e.filename}:${e.lineno}:${e.colno}` : '';
      const stack = e.stack ? `\n  ${e.stack.split('\n').slice(0, 6).join('\n  ')}` : '';
      const url = e.url ? `\n  url: ${e.url}` : '';
      return `${i + 1}. [${ts}] ${e.kind}: ${e.message}${loc}${url}${stack}`;
    })
    .join('\n\n');
}
